import { inject, Injectable, signal } from '@angular/core';
import { FormControl } from '@angular/forms';

import { Store } from '@ngrx/store';
import { Subscription, tap } from 'rxjs';

import { DevFlagSelectors } from '@core/dev-flags-data';
import { DevFlag } from '@core/dev-flags-types';
import { AutomationConditionType } from '@core/workflow-automations-types';

import { AutomationOptionsService } from './automation-options.service';
import { AutomationsWizardDialogFormService } from './automations-wizard-dialog-form.service';

@Injectable()
export class AutomationConditionsManagerService {
  private readonly automationOptionsService = inject(AutomationOptionsService);
  private readonly formService = inject(AutomationsWizardDialogFormService);
  private readonly store = inject(Store);

  public hasWorkflowAutomationImprovementsFlag = this.store.selectSignal(
    DevFlagSelectors.isEnabled(DevFlag.WorkflowAutomationImprovements),
  );

  private currentConditionsOptions = this.automationOptionsService.currentConditionsOptions;

  private subscriptions = signal<Subscription[]>([]);

  constructor() {
    this.clearSubscriptions();
  }

  public listenToConditionTypeChanges(typeControl: FormControl<AutomationConditionType | null>) {
    const controlIndex = this.subscriptions().length;
    const subscription = typeControl.valueChanges
      .pipe(
        tap(conditionType => {
          this.buildForm(conditionType, controlIndex);
        }),
      )
      .subscribe();
    this.subscriptions.update(subscriptions => [...subscriptions, subscription]);
  }

  public stopListeningToConditionTypeChanges(controlIndex: number) {
    this.subscriptions.update(subscriptions =>
      subscriptions.filter((subscription, index) => {
        if (controlIndex === index) {
          subscription.unsubscribe();
          return false;
        }
        return true;
      }),
    );
  }

  public clearSubscriptions() {
    this.subscriptions().forEach(sub => sub.unsubscribe());
    this.subscriptions.set([]);
  }

  public ngOnDestroy(): void {
    this.clearSubscriptions();
  }

  private buildForm(conditionType: AutomationConditionType | null, controlIndex: number): void {
    const currentConditionOptions = this.currentConditionsOptions().find(
      conditionOptions => conditionOptions.type === conditionType,
    );

    if (!currentConditionOptions) {
      return;
    }

    this.formService.buildConditionDataForm(currentConditionOptions, controlIndex);
  }
}
